import type { MessageDescriptor } from '@lingui/core';
import { ApiError, toApiError } from './api-error';
import { BackendErrorCode } from './backend-error-code';
import {
  BACKEND_ERROR_MESSAGES,
  BACKEND_ERROR_TEXT_MESSAGES,
  BACKEND_ERROR_TEXT_RULES,
  CLIENT_ERROR_MESSAGES,
} from './backend-error-messages';
import { BackendErrorContext, BackendErrorText } from './backend-error-text';
import { ClientErrorCode } from './client-error-code';

export type ErrorReasonOverrides = Partial<Record<BackendErrorCode | BackendErrorText | ClientErrorCode, MessageDescriptor>>;

export interface BackendErrorTextOptions {
  /** Включает правила «по тексту», привязанные к этому месту (`BackendErrorContext`). */
  context?: BackendErrorContext;
}

export interface GetErrorReasonOptions extends BackendErrorTextOptions {
  /** Текст, который на этом экране точнее общего: `{ [BackendErrorCode.NOT_ALLOWED]: msg`…` }`. */
  overrides?: ErrorReasonOverrides;
}

function findBackendErrorText({ code, message }: ApiError, context?: BackendErrorContext): BackendErrorText | undefined {
  return BACKEND_ERROR_TEXT_RULES.find(
    (rule) => rule.code === code && (rule.context === undefined || rule.context === context) && rule.match(message),
  )?.text;
}

/**
 * Случай раздела «по тексту» (`BackendErrorText`) — для отказа без кода или кода с несколькими
 * причинами. По нему фича ветвится, как по коду: сама она текст ошибки не сравнивает.
 */
export function getBackendErrorText(error: unknown, options: BackendErrorTextOptions = {}): BackendErrorText | undefined {
  const apiError = toApiError(error);
  return apiError.clientCode === undefined ? findBackendErrorText(apiError, options.context) : undefined;
}

/**
 * Переведённая причина ошибки — или `undefined`, если перевода нет (кода нет в ответе или в
 * реестре). Тогда место вызова показывает только свой заголовок: английский текст бэкенда
 * пользователю не показываем.
 *
 * Порядок: ошибка фронта → случай «по тексту» (он точнее общего текста своего кода) → код.
 * На каждом шаге `overrides` места вызова сильнее реестра.
 */
export function getErrorReason(error: unknown, options: GetErrorReasonOptions = {}): MessageDescriptor | undefined {
  const apiError = toApiError(error);
  const { code, clientCode } = apiError;
  const { overrides, context } = options;

  if (clientCode !== undefined) return overrides?.[clientCode] ?? CLIENT_ERROR_MESSAGES[clientCode];

  const text = findBackendErrorText(apiError, context);
  if (text !== undefined) return overrides?.[text] ?? BACKEND_ERROR_TEXT_MESSAGES[text];

  if (code === undefined) return undefined;

  return overrides?.[code as BackendErrorCode] ?? BACKEND_ERROR_MESSAGES[code as BackendErrorCode];
}
